if (!customElements.get('product-addons')) {
  class ProductAddons extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.items = this.querySelectorAll('[data-addon-item]');
      this.productPriceElement = this.querySelector('[data-product-price]');
      this.addonsTotalElement = this.querySelector('[data-addons-total]');
      this.orderTotalElement = this.querySelector('[data-order-total]');
      this.productPrice = parseInt(this.productPriceElement.dataset.productPrice, 10) || 0;

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onChange);

      // Capturing on the document runs ahead of the product form's own submit handler,
      // so the selection can be added in the same request as the product.
      document.addEventListener('submit', this.onDocumentSubmit, true);

      this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.onVariantChange
      );
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.onDocumentSubmit, true);
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
    }

    get productForm() {
      return document.querySelector(
        `product-info[data-section="${this.sectionId}"] form[data-type="add-to-cart-form"]`
      );
    }

    onClick = (event) => {
      const toggle = event.target.closest('[data-addon-toggle]');
      if (!toggle || toggle.disabled) return;

      const selected = toggle.getAttribute('aria-pressed') !== 'true';
      toggle.setAttribute('aria-pressed', String(selected));
      toggle.closest('[data-addon-item]').classList.toggle('is-selected', selected);
      toggle.querySelector('[data-addon-toggle-text]').textContent = selected
        ? this.dataset.addedLabel
        : this.dataset.addLabel;

      this.renderTotals();
    };

    onChange = (event) => {
      if (!event.target.closest('[data-addon-item]')) return;

      const variantSelect = event.target.closest('select[name="id"]');
      if (variantSelect) {
        const item = variantSelect.closest('[data-addon-item]');
        const option = variantSelect.selectedOptions[0];
        item.dataset.variantId = option.value;
        item.dataset.price = option.dataset.price;
      }

      this.renderTotals();
    };

    onVariantChange = (event) => {
      const data = event?.data;
      if (!data || data.sectionId !== this.sectionId) return;
      // An unavailable option combination reports no variant, so keep the last known price.
      if (!data.variant) return;

      this.productPrice = data.variant.price;
      this.renderTotals();
    };

    getSelection() {
      return Array.from(this.items)
        .filter((item) => item.querySelector('[data-addon-toggle]').getAttribute('aria-pressed') === 'true')
        .map((item) => {
          const quantityInput = item.querySelector('[name="quantity"]');
          const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

          return {
            id: parseInt(item.dataset.variantId, 10),
            quantity: quantity > 0 ? quantity : 1,
            price: parseInt(item.dataset.price, 10) || 0
          };
        });
    }

    renderTotals() {
      const format = (cents) => FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
      const addonsTotal = this.getSelection().reduce((total, item) => total + item.price * item.quantity, 0);

      this.productPriceElement.innerHTML = format(this.productPrice);
      this.addonsTotalElement.innerHTML = format(addonsTotal);
      this.orderTotalElement.innerHTML = format(this.productPrice + addonsTotal);
    }

    clearSelection() {
      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-addon-toggle]');
        toggle.setAttribute('aria-pressed', 'false');
        toggle.querySelector('[data-addon-toggle-text]').textContent = this.dataset.addLabel;
        item.classList.remove('is-selected');
      });

      this.renderTotals();
    }

    onDocumentSubmit = (event) => {
      const form = event.target;
      if (form !== this.productForm) return;
      if (FoxTheme.settings.cartType === 'page') return;

      const selection = this.getSelection();
      if (selection.length === 0) return;

      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton.getAttribute('aria-disabled') === 'true') return;

      event.preventDefault();
      event.stopPropagation();

      this.lastSubmittedElement = event.submitter || submitButton;
      this.displayFormErrors(form);

      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('btn--loading');

      this.addToCart(form, selection).finally(() => {
        submitButton.classList.remove('btn--loading');
        submitButton.removeAttribute('aria-disabled');
      });
    };

    buildItems(form, selection) {
      const formData = new FormData(form);
      const quantity = parseInt(formData.get('quantity'), 10);
      const product = { id: parseInt(formData.get('id'), 10), quantity: quantity > 0 ? quantity : 1 };

      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      });
      if (Object.keys(properties).length > 0) product.properties = properties;

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) product.selling_plan = parseInt(sellingPlan, 10);

      return [product, ...selection.map(({ id, quantity: itemQuantity }) => ({ id, quantity: itemQuantity }))];
    }

    async addToCart(form, selection) {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
      );

      const config = FoxTheme.utils.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        items: this.buildItems(form, selection),
        sections,
        sections_url: window.location.pathname
      });

      try {
        const response = await fetch(`${FoxTheme.routes.cart_add_url}`, config);
        const parsedState = await response.json();

        if (parsedState.status) {
          this.displayFormErrors(form, parsedState.description || parsedState.message);
          return;
        }

        const cartJson = await (
          await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
        ).json();
        cartJson['sections'] = parsedState['sections'];

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

        this.clearSelection();

        const cartDrawer = document.querySelector('cart-drawer');
        cartDrawer && cartDrawer.show(this.lastSubmittedElement);
      } catch (error) {
        console.log(error);
      }
    }

    displayFormErrors(form, errorMessage = false) {
      const errorElement = form.querySelector('.product-form__error-message');
      if (!errorElement) {
        errorMessage && alert(errorMessage);
        return;
      }

      errorElement.toggleAttribute('hidden', !errorMessage);
      if (errorMessage !== false) errorElement.innerText = errorMessage;
    }
  }

  customElements.define('product-addons', ProductAddons);
}
